import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { annualReconciliationAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './AnnualReconciliationCreate.css';

const AnnualReconciliationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    documentDate: new Date().toISOString().split('T')[0],
    issuedIn: '',
    firstPartyName: '',
    firstPartyIdNumber: '',
    firstPartyNationality: '',
    secondPartyName: '',
    propertyType: '',
    propertyDistrict: '',
    plotNumber: '',
    periodFrom: new Date().toISOString().split('T')[0],
    periodUntil: new Date().toISOString().split('T')[0],
    isActive: 'true',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadAnnualReconciliation();
  }, [id]);

  const loadAnnualReconciliation = async () => {
    try {
      setLoadingData(true);
      const response = await annualReconciliationAPI.getById(id);
      const data = response.data;
      setFormData({
        ...data,
        documentDate: data.documentDate ? new Date(data.documentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        periodFrom: data.periodFrom ? new Date(data.periodFrom).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        periodUntil: data.periodUntil ? new Date(data.periodUntil).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('annualReconciliation.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.documentDate) {
      newErrors.documentDate = t('annualReconciliation.edit.errors.documentDateRequired');
    }
    if (!formData.firstPartyName.trim()) {
      newErrors.firstPartyName = t('annualReconciliation.edit.errors.firstPartyNameRequired');
    }
    if (!formData.firstPartyIdNumber.trim()) {
      newErrors.firstPartyIdNumber = t('annualReconciliation.edit.errors.firstPartyIdNumberRequired');
    }
    if (!formData.periodFrom) {
      newErrors.periodFrom = t('annualReconciliation.edit.errors.periodFromRequired');
    }
    if (!formData.periodUntil) {
      newErrors.periodUntil = t('annualReconciliation.edit.errors.periodUntilRequired');
    }
    if (new Date(formData.periodUntil) < new Date(formData.periodFrom)) {
      newErrors.periodUntil = t('annualReconciliation.edit.errors.periodUntilBeforePeriodFrom');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        isActive: formData.isActive === 'true',
      };
      await annualReconciliationAPI.update(id, submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/annual-reconciliation');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(err.response?.data?.message || t('annualReconciliation.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Document Information */}
          <h3 className="form-section-title">{t('annualReconciliation.edit.sections.documentInformation')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('annualReconciliation.edit.fields.documentDate')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.documentDate ? 'is-invalid' : ''}`}
                  name="documentDate"
                  value={formData.documentDate}
                  onChange={handleChange}
                />
              </div>
              {errors.documentDate && (
                <div className="error-message">{errors.documentDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marker-alt"></i> {t('annualReconciliation.edit.fields.issuedIn')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marker-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="issuedIn"
                  value={formData.issuedIn}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.issuedIn')}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('annualReconciliation.edit.fields.isActive')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <select
                  className="form-input"
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* First Party */}
          <h3 className="form-section-title">{t('annualReconciliation.edit.sections.firstParty')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('annualReconciliation.edit.fields.firstPartyName')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.firstPartyName ? 'is-invalid' : ''}`}
                  name="firstPartyName"
                  value={formData.firstPartyName}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.firstPartyName')}
                  maxLength={255}
                />
              </div>
              {errors.firstPartyName && (
                <div className="error-message">{errors.firstPartyName}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-card"></i> {t('annualReconciliation.edit.fields.firstPartyIdNumber')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-card input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.firstPartyIdNumber ? 'is-invalid' : ''}`}
                  name="firstPartyIdNumber"
                  value={formData.firstPartyIdNumber}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.firstPartyIdNumber')}
                  maxLength={50}
                />
              </div>
              {errors.firstPartyIdNumber && (
                <div className="error-message">{errors.firstPartyIdNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-flag"></i> {t('annualReconciliation.edit.fields.firstPartyNationality')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-flag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyNationality"
                  value={formData.firstPartyNationality}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.firstPartyNationality')}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          {/* Second Party */}
          <h3 className="form-section-title">{t('annualReconciliation.edit.sections.secondParty')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('annualReconciliation.edit.fields.secondPartyName')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="secondPartyName"
                  value={formData.secondPartyName}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.secondPartyName')}
                  maxLength={255}
                />
              </div>
            </div>
          </div>

          {/* Property Information */}
          <h3 className="form-section-title">{t('annualReconciliation.edit.sections.propertyInformation')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('annualReconciliation.edit.fields.propertyType')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.propertyType')}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-map-marked-alt"></i> {t('annualReconciliation.edit.fields.propertyDistrict')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-map-marked-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="propertyDistrict"
                  value={formData.propertyDistrict}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.propertyDistrict')}
                  maxLength={255}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-hashtag"></i> {t('annualReconciliation.edit.fields.plotNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-hashtag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="plotNumber"
                  value={formData.plotNumber}
                  onChange={handleChange}
                  placeholder={t('annualReconciliation.edit.fields.plotNumber')}
                  maxLength={50}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('annualReconciliation.edit.fields.periodFrom')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.periodFrom ? 'is-invalid' : ''}`}
                  name="periodFrom"
                  value={formData.periodFrom}
                  onChange={handleChange}
                />
              </div>
              {errors.periodFrom && (
                <div className="error-message">{errors.periodFrom}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('annualReconciliation.edit.fields.periodUntil')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.periodUntil ? 'is-invalid' : ''}`}
                  name="periodUntil"
                  value={formData.periodUntil}
                  onChange={handleChange}
                />
              </div>
              {errors.periodUntil && (
                <div className="error-message">{errors.periodUntil}</div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/annual-reconciliation')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('annualReconciliation.edit.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('annualReconciliation.edit.messages.updateSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default AnnualReconciliationEdit;
